import type { QueryClient } from '@tanstack/react-query'
import type { Address } from 'viem'

import type { UserLendPositions } from '@/lib/procedures/userPositions.contract'

import { orpc } from '@/lib/orpc/client'

import { isUserQuery } from './isUserQuery'

/** Delays before each attempt, in ms. Roughly a minute of chasing, front-loaded. */
const INDEXING_DELAYS_MS = [0, 5_000, 10_000, 15_000, 30_000]

/**
 * Chases a freshly-supplied position through the indexer behind `user.lendPositions`. Invalidation only
 * guarantees one refetch, and `useLendPositionsTableData` turns polling off while the result
 * is empty, so a first-ever supply needs a bounded retry window of its own.
 *
 * Both the refetch and stop check are scoped to one account so another wallet's cached rows cannot
 * make this supply appear indexed after an account switch.
 *
 * With `market`, the chase ends only once that market's row is present: an account that
 * already lends elsewhere gets a non-empty page straight away, which says nothing about the
 * new position. Without it (in-kind redemption, which lands in several markets), any row does.
 */
export const refetchLendPositionsUntilIndexed = async (
  queryClient: QueryClient,
  userAddress: Address | undefined,
  market?: { chainId: number; marketId: string },
  delays: number[] = INDEXING_DELAYS_MS
) => {
  if (!userAddress) return
  const filters = {
    queryKey: orpc.user.lendPositions.key({ type: 'query' }),
    predicate: isUserQuery(userAddress)
  }
  const marketId = market?.marketId.toLowerCase()
  const isTarget = (item: UserLendPositions['items'][number]) =>
    !market ||
    (item.market.chainId === market.chainId && item.market.marketId.toLowerCase() === marketId)

  for (const delay of delays) {
    if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay))
    await queryClient.refetchQueries(filters)
    const isIndexed = queryClient
      .getQueriesData<UserLendPositions>(filters)
      .some(([, data]) => data?.items.some(isTarget) ?? false)
    if (isIndexed) break
  }

  // Both exits leave a fresh `dataUpdatedAt` on the variants that were refetched, so the
  // table's 60s `staleTime` would serve the others (a saved sort, another page) their
  // pre-transaction rows. Marking stale without refetching makes the next mount pick them up.
  await queryClient.invalidateQueries({ ...filters, refetchType: 'none' })
}
